import React, { useCallback, useEffect, useState } from 'react';
import { Form, Button, Modal, Spinner, Alert, Badge } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import Select from './common/Select';
import {
  useGetCommissionsProjections,
  postCommissionsProjections,
  patchCommissionsProjectionsId,
  deleteCommissionsProjectionsId,
  postCommissionsProjectionsIdRecalculate,
  getGetCommissionsProjectionsQueryKey,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Projection } from '../api/generated/model';
import { useSyncStateBySyncId } from '../hooks/useSyncState';

// --- shared form types -------------------------------------------------

interface ProjectionForm {
  name: string;
  start_date: string;
  end_date: string;
  assume_invoice_validated: boolean;
  assume_po_or_waiver: boolean;
  assume_delivery_by_date: string;
  assume_delivery_undated: boolean;
  show_mode: 'actual_only' | 'projected_only' | 'union';
}

function projectionToForm(p: Projection): ProjectionForm {
  return {
    name: p.name ?? '',
    start_date: p.start_date ?? '',
    end_date: p.end_date ?? '',
    assume_invoice_validated: p.assume_invoice_validated ?? false,
    assume_po_or_waiver: p.assume_po_or_waiver ?? false,
    assume_delivery_by_date: p.assume_delivery_by_date ?? '',
    assume_delivery_undated: p.assume_delivery_undated ?? false,
    show_mode: (p.show_mode ?? 'union') as 'actual_only' | 'projected_only' | 'union',
  };
}

function formDiff(original: ProjectionForm, edited: ProjectionForm): Array<{ op: 'replace'; path: string; value: unknown }> {
  const patch: Array<{ op: 'replace'; path: string; value: unknown }> = [];
  const fields: Array<keyof ProjectionForm> = [
    'name', 'start_date', 'end_date',
    'assume_invoice_validated', 'assume_po_or_waiver',
    'assume_delivery_by_date', 'assume_delivery_undated',
    'show_mode',
  ];
  for (const f of fields) {
    if (original[f] !== edited[f]) {
      // Date fields: empty string ⇒ JSON null (clears the field per
      // the backend patch handler's contract).
      const dateField = f === 'start_date' || f === 'end_date' || f === 'assume_delivery_by_date';
      const v = dateField && edited[f] === '' ? null : edited[f];
      patch.push({ op: 'replace', path: `/${f}`, value: v });
    }
  }
  return patch;
}

interface ProjectionOption {
  value: string;
  label: string;
}

const LIVE_OPTION: ProjectionOption = { value: '', label: 'Live (no projection)' };

// --- ProjectionPicker: compact dropdown + Create button, fits in a header ----

interface ProjectionPickerProps {
  selectedId: string | undefined;
  onSelect: (id: string | undefined) => void;
}

export const ProjectionPicker: React.FC<ProjectionPickerProps> = ({ selectedId, onSelect }) => {
  const queryClient = useQueryClient();
  // sort_field / sort_direction are required by the cursor library on
  // first-page requests (see feedback_cursor_pagination).
  const { data: listData, isLoading } = useGetCommissionsProjections({
    sort_field: 'name',
    sort_direction: 'asc',
  });
  const projections: Projection[] = (listData as any)?.data ?? [];

  const options: ProjectionOption[] = [
    LIVE_OPTION,
    ...projections.filter((p) => p.id).map((p) => ({ value: p.id!, label: p.name ?? p.id! })),
  ];
  const selectedOption = options.find((o) => o.value === (selectedId ?? '')) ?? LIVE_OPTION;

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreate = useCallback(async () => {
    const name = newName.trim();
    if (!name) {
      setCreateError('Name is required.');
      return;
    }
    try {
      const res = await postCommissionsProjections({ name });
      const created = res as Projection;
      await queryClient.invalidateQueries({ queryKey: getGetCommissionsProjectionsQueryKey() });
      setShowCreate(false);
      setNewName('');
      if (created.id) onSelect(created.id);
    } catch (err: any) {
      setCreateError(err?.message ?? 'Create failed');
    }
  }, [newName, queryClient, onSelect]);

  return (
    <>
      <div className="d-flex align-items-center gap-1" style={{ minWidth: 240 }}>
        <Select<ProjectionOption>
          options={options}
          value={selectedOption}
          onChange={(opt) => onSelect(opt && opt.value ? opt.value : undefined)}
          isClearable={false}
          isLoading={isLoading}
          blurInputOnSelect
        />
        <Button
          variant="outline-primary"
          size="sm"
          title="Create new projection"
          onClick={() => { setNewName(''); setCreateError(null); setShowCreate(true); }}
        >
          <i className="bi bi-plus-lg"></i>
        </Button>
      </div>

      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create projection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Q3 + delivery override"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) handleCreate(); }}
            />
            <Form.Text className="text-muted">
              You can edit params + recalculate after creating.
            </Form.Text>
          </Form.Group>
          {createError && <Alert variant="danger" className="mt-3 mb-0">{createError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} disabled={!newName.trim()}>Create</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// --- ProjectionFormModal: full params editor in a modal ---

interface ProjectionFormPanelProps {
  selectedId: string;
  show: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const ProjectionFormPanel: React.FC<ProjectionFormPanelProps> = ({ selectedId, show, onClose, onDeleted }) => {
  const queryClient = useQueryClient();
  const { data: listData } = useGetCommissionsProjections({
    sort_field: 'name',
    sort_direction: 'asc',
  });
  const projections: Projection[] = (listData as any)?.data ?? [];
  const selected = projections.find((p) => p.id === selectedId);

  const [form, setForm] = useState<ProjectionForm | null>(null);
  const [originalForm, setOriginalForm] = useState<ProjectionForm | null>(null);
  useEffect(() => {
    if (selected) {
      const f = projectionToForm(selected);
      setForm(f);
      setOriginalForm(f);
    } else {
      setForm(null);
      setOriginalForm(null);
    }
  }, [selected]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [recalcSyncId, setRecalcSyncId] = useState<string | undefined>(undefined);
  const syncState = useSyncStateBySyncId(recalcSyncId);
  const isRecalculating = !!(syncState && (syncState.status === 'started' || syncState.status === 'progress'));
  const recalcCompleted = !!(syncState && syncState.status === 'completed');

  useEffect(() => {
    if (recalcCompleted) {
      queryClient.invalidateQueries({ queryKey: getGetCommissionsProjectionsQueryKey() });
      // CommissionsPage feeds usePaginatedQuery a string prefix
      // `projection-invoices-${id}`; the hook builds the real
      // react-query key as `[prefix, queryParams]` and invalidates
      // with `[prefix]` for prefix-matching. Mirror that here so the
      // datatable refetches when the recompute finishes.
      queryClient.invalidateQueries({ queryKey: [`projection-invoices-${selectedId}`] });
      setRecalcSyncId(undefined);
    }
  }, [recalcCompleted, queryClient, selectedId]);

  const handleSave = useCallback(async (recalculate: boolean) => {
    if (!selected || !form || !originalForm) return;
    const patch = formDiff(originalForm, form);
    setSaving(true);
    setSaveError(null);
    try {
      if (patch.length > 0) {
        await patchCommissionsProjectionsId(selected.id!, patch);
      }
      await queryClient.invalidateQueries({ queryKey: getGetCommissionsProjectionsQueryKey() });
      setOriginalForm(form);
      if (recalculate) {
        const res = await postCommissionsProjectionsIdRecalculate(selected.id!);
        if ((res as any)?.sync_id) {
          setRecalcSyncId((res as any).sync_id as string);
        }
      }
    } catch (err: any) {
      setSaveError(err?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [selected, form, originalForm, queryClient]);

  const handleDelete = useCallback(async () => {
    if (!selected || !selected.id) return;
    if (!window.confirm(`Delete projection "${selected.name}"?`)) return;
    try {
      await deleteCommissionsProjectionsId(selected.id);
      await queryClient.invalidateQueries({ queryKey: getGetCommissionsProjectionsQueryKey() });
      onDeleted();
    } catch (err: any) {
      setSaveError(err?.message ?? 'Delete failed');
    }
  }, [selected, queryClient, onDeleted]);

  if (!form || !selected) return null;
  const dirty = !!originalForm && formDiff(originalForm, form).length > 0;

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-sliders me-2 text-primary"></i>
          Edit projection: {selected.name}
          {dirty && <Badge bg="warning" className="ms-2">Unsaved changes</Badge>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-between text-muted small mb-3">
          {selected.last_recalculated_at ? (
            <span>Last recalculated: {new Date(selected.last_recalculated_at).toLocaleString()}</span>
          ) : (
            <span>Never recalculated.</span>
          )}
          {isRecalculating && (
            <span className="text-info d-flex align-items-center gap-1">
              <Spinner animation="border" size="sm" /> Recalculating…
            </span>
          )}
        </div>

        <div className="d-flex gap-3 flex-wrap align-items-end mb-3">
          <Form.Group style={{ flex: '1 1 240px' }}>
            <Form.Label className="small mb-1">Name</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small mb-1">Start date</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small mb-1">End date</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small mb-1">Show mode</Form.Label>
            <Form.Select
              size="sm"
              value={form.show_mode}
              onChange={(e) => setForm({ ...form, show_mode: e.target.value as ProjectionForm['show_mode'] })}
            >
              <option value="union">Union (actual + projected)</option>
              <option value="actual_only">Actual only (cleared in live)</option>
              <option value="projected_only">Projected only (blocked in live)</option>
            </Form.Select>
          </Form.Group>
        </div>

        <div className="d-flex gap-3 flex-wrap align-items-end mb-2">
          <Form.Check
            type="checkbox"
            id="projection-assume-invoice-validated"
            label="Assume invoice validated"
            checked={form.assume_invoice_validated}
            onChange={(e) => setForm({ ...form, assume_invoice_validated: e.target.checked })}
          />
          <Form.Check
            type="checkbox"
            id="projection-assume-po-or-waiver"
            label="Assume PO/waiver passed"
            checked={form.assume_po_or_waiver}
            onChange={(e) => setForm({ ...form, assume_po_or_waiver: e.target.checked })}
          />
          <Form.Check
            type="checkbox"
            id="projection-assume-delivery-undated"
            label="Assume undated lines delivered"
            checked={form.assume_delivery_undated}
            onChange={(e) => setForm({ ...form, assume_delivery_undated: e.target.checked })}
          />
          <Form.Group>
            <Form.Label className="small mb-1">Assume dated delivery as of</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={form.assume_delivery_by_date}
              onChange={(e) => setForm({ ...form, assume_delivery_by_date: e.target.value })}
            />
          </Form.Group>
        </div>

        {saveError && <Alert variant="danger" className="mt-2 mb-0 small">{saveError}</Alert>}
        {selected.last_recalculate_error && (
          <Alert variant="warning" className="mt-2 mb-0 small">
            Last recalculate error: {selected.last_recalculate_error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        {/* Save & Recalculate on the LEFT, Delete pulled to the FAR RIGHT
            with a deliberate gap so it's not adjacent to the primary
            action — Delete is destructive and shouldn't be a one-pixel
            mis-click away from "Save". */}
        <Button
          variant="primary"
          disabled={saving || isRecalculating}
          onClick={() => handleSave(true)}
        >
          {isRecalculating ? 'Recalculating…' : 'Save & Recalculate'}
        </Button>
        <Button
          variant="outline-danger"
          disabled={saving}
          onClick={handleDelete}
        >
          <i className="bi bi-trash me-1"></i>
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Default export: picker + an edit-icon button that opens the params
// modal. Prefer importing ProjectionPicker and ProjectionFormPanel
// separately when placing them in a custom layout (the team-view
// renders the picker in the DataTable header and triggers the modal
// from there).
interface ProjectionsPanelProps {
  selectedId: string | undefined;
  onSelect: (id: string | undefined) => void;
}
const ProjectionsPanel: React.FC<ProjectionsPanelProps> = ({ selectedId, onSelect }) => {
  const [showForm, setShowForm] = useState(false);
  return (
    <>
      <div className="d-flex align-items-center gap-1">
        <ProjectionPicker selectedId={selectedId} onSelect={onSelect} />
        {selectedId && (
          <Button
            variant="outline-secondary"
            size="sm"
            title="Edit projection params"
            onClick={() => setShowForm(true)}
          >
            <i className="bi bi-gear"></i>
          </Button>
        )}
      </div>
      {selectedId && (
        <ProjectionFormPanel
          selectedId={selectedId}
          show={showForm}
          onClose={() => setShowForm(false)}
          onDeleted={() => { setShowForm(false); onSelect(undefined); }}
        />
      )}
    </>
  );
};
export default ProjectionsPanel;
